/**
 * Merowen Contact Us page-owned form enhancement.
 * Shopify owns delivery and server responses; this module adds accessible
 * client validation, message counting, and the transient loading state.
 */

const mhContactPageSelector = "#MainContent[data-template='page.contact-merowen']";

function mhContactInitializeForm(scope = document) {
  const mhContactPage = scope.matches?.(mhContactPageSelector)
    ? scope
    : scope.querySelector?.(mhContactPageSelector) || document.querySelector(mhContactPageSelector);

  if (!mhContactPage) return;

  mhContactPage.dataset.mhContactReady = 'true';

  const form = mhContactPage.querySelector('#mh-contact-form-element');
  const formFields = form?.querySelector('[data-mh-contact-form]');

  if (!form || !formFields || form.dataset.mhContactInitialized === 'true') return;

  form.dataset.mhContactInitialized = 'true';
  form.noValidate = true;

  const topicFieldset = form.querySelector('.mh-contact__topic-fieldset');
  const topicInputs = Array.from(form.querySelectorAll("input[name='contact[Topic]']"));
  const nameInput = form.querySelector('#mh-contact-full-name');
  const emailInput = form.querySelector('#mh-contact-email');
  const messageInput = form.querySelector('#mh-contact-message');
  const messageCounter = form.querySelector('#mh-contact-message-counter');
  const submitButton = form.querySelector('[data-mh-contact-submit]');
  const submitLabel = form.querySelector('[data-mh-contact-submit-label]');
  const errorSummary = form.querySelector('[data-mh-contact-error-summary]');
  const successNotice = form.querySelector('[data-mh-contact-success]');
  const initialSubmitLabel = submitLabel?.textContent || '';

  const errorElements = {
    topic: form.querySelector("[data-mh-contact-error-for='topic']"),
    name: form.querySelector("[data-mh-contact-error-for='name']"),
    email: form.querySelector("[data-mh-contact-error-for='email']"),
    message: form.querySelector("[data-mh-contact-error-for='message']"),
  };

  const messages = {
    topic: formFields.dataset.topicRequiredMessage || 'Choose a topic.',
    name: formFields.dataset.nameRequiredMessage || 'Enter your full name.',
    emailRequired: formFields.dataset.emailRequiredMessage || 'Enter your email address.',
    emailInvalid: formFields.dataset.emailInvalidMessage || 'Enter a valid email address.',
    message: formFields.dataset.messageRequiredMessage || 'Enter a message.',
  };

  const setError = (key, message = '') => {
    const error = errorElements[key];
    const invalid = Boolean(message);

    if (error) {
      error.textContent = message;
      error.hidden = !invalid;
    }

    if (key === 'topic') {
      topicFieldset?.setAttribute('aria-invalid', String(invalid));
      topicInputs.forEach((input) => input.setAttribute('aria-invalid', String(invalid)));
      return;
    }

    const input = { name: nameInput, email: emailInput, message: messageInput }[key];
    input?.setAttribute('aria-invalid', String(invalid));
  };

  const updateCounter = () => {
    if (!messageInput || !messageCounter) return;
    messageCounter.textContent = `${messageInput.value.length} / 1000`;
  };

  const validateTopic = () => {
    const valid = topicInputs.some((input) => input.checked);
    setError('topic', valid ? '' : messages.topic);
    return valid;
  };

  const validateName = () => {
    const valid = Boolean(nameInput?.value.trim());
    setError('name', valid ? '' : messages.name);
    return valid;
  };

  const validateEmail = () => {
    const value = emailInput?.value.trim() || '';
    const message = !value
      ? messages.emailRequired
      : emailInput.validity.typeMismatch
        ? messages.emailInvalid
        : '';
    setError('email', message);
    return !message;
  };

  const validateMessage = () => {
    const valid = Boolean(messageInput?.value.trim());
    setError('message', valid ? '' : messages.message);
    return valid;
  };

  const hideSummaryWhenResolved = () => {
    const hasVisibleError = Object.values(errorElements).some((error) => error && !error.hidden);
    if (!hasVisibleError && errorSummary) errorSummary.hidden = true;
  };

  topicInputs.forEach((input) => {
    input.addEventListener('change', () => {
      validateTopic();
      hideSummaryWhenResolved();
    });
  });

  nameInput?.addEventListener('input', () => {
    if (nameInput.getAttribute('aria-invalid') === 'true') validateName();
    hideSummaryWhenResolved();
  });

  emailInput?.addEventListener('input', () => {
    if (emailInput.getAttribute('aria-invalid') === 'true') validateEmail();
    hideSummaryWhenResolved();
  });

  messageInput?.addEventListener('input', () => {
    updateCounter();
    if (messageInput.getAttribute('aria-invalid') === 'true') validateMessage();
    hideSummaryWhenResolved();
  });

  updateCounter();

  form.addEventListener('submit', (event) => {
    errorSummary?.querySelector('[data-mh-contact-server-errors]')?.remove();

    const results = [validateTopic(), validateName(), validateEmail(), validateMessage()];
    const valid = results.every(Boolean);

    if (!valid) {
      event.preventDefault();
      if (errorSummary) {
        errorSummary.hidden = false;
        errorSummary.focus();
      }
      return;
    }

    form.setAttribute('aria-busy', 'true');
    if (submitButton) submitButton.disabled = true;
    if (submitLabel) {
      submitLabel.textContent = submitButton?.dataset.loadingLabel || 'Sending...';
    }
  });

  window.addEventListener('pageshow', () => {
    form.removeAttribute('aria-busy');
    if (submitButton) submitButton.disabled = false;
    if (submitLabel) submitLabel.textContent = initialSubmitLabel;
  });

  if (successNotice) {
    requestAnimationFrame(() => successNotice.focus());
  } else if (errorSummary && !errorSummary.hidden) {
    requestAnimationFrame(() => errorSummary.focus());
  }
}

mhContactInitializeForm();

document.addEventListener('shopify:section:load', (event) => {
  mhContactInitializeForm(event.target);
});
